import type React from 'react';
import { Button, Icon } from 'semantic-ui-react';

import { setTourProps } from '@store/actions/action-tour';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectIsAdminFromAccount } from '@store/reducers/reducer-auth';
import {
  TOUR_BUTTON_LABEL,
  ROUTE_CONTEXT_SEGMENTS_TO_REMOVE,
} from '@features/app-tour/app-tour.const';
import type { TourConfig } from '@features/app-tour/app-tour.models';
import { useTourConfig } from '@features/app-tour/hooks';

export interface ShowTourProps {
  routeMask: string;
  hash?: string;
}

const ShowTour = ({ routeMask, hash = '' }: ShowTourProps) => {
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(selectIsAdminFromAccount);
  const app = useAppSelector(state => state.app);

  // Process routeMask as a string.
  const routeMaskTmp = routeMask.split('/');
  routeMaskTmp.splice(0, ROUTE_CONTEXT_SEGMENTS_TO_REMOVE);
  const baseKey = `/${routeMaskTmp.join('/')}`;

  // Create the tour key with hash if provided
  const tourKeyWithHash = hash ? `${baseKey}${hash}` : baseKey;

  // Get tour configuration - first try with hash, then without
  const tourConfiguration = useTourConfig();
  let tourData = tourConfiguration[tourKeyWithHash as keyof TourConfig];
  let tourKey = tourKeyWithHash;

  // If no hash-specific tour found, fall back to base route
  if (!tourData && hash) {
    tourData = tourConfiguration[baseKey as keyof TourConfig];
    tourKey = baseKey;
  }

  const { tour } = app;
  let el: React.ReactElement | null = null;

  // If there is a tour for this route and the user is an admin or the tour is
  // not admin only, AND the tour is not marked as `oneTimeOnly`, show the tour
  // button
  if (
    tourData &&
    ((tourData[0].adminOnly && isAdmin) || !tourData[0].adminOnly) &&
    !tourData[0].oneTimeOnly
  ) {
    el = (
      <Button
        fluid
        size="tiny"
        onClick={() => {
          dispatch(
            setTourProps({
              ...tour.tourProps,
              [tourKey]: {
                isDisabled: false,
                showIntro: true,
              },
            }),
          );
        }}
      >
        <Icon name="info circle" />
        {TOUR_BUTTON_LABEL}
      </Button>
    );
  }

  return el;
};

export default ShowTour;
